/**
 * Marketplace filtering and sorting.
 * ----------------------------------
 * Kept separate from the component so the rules can be unit tested directly
 * (see test-marketplace-filters.js) instead of only being exercised by hand in
 * a browser.
 *
 * The one rule worth reading carefully is the free / paid split:
 *
 *   price === 0 (or missing)  ->  "free"  -> only shown by the Free filter
 *   price > 0 (even $0.01)    ->  "paid"  -> shown by both price sorts
 *
 * So "Price: low to high" deliberately does NOT surface $0 products as the
 * cheapest option. A free product is not the cheapest paid product, it is a
 * different thing, and mixing them would make the sort look broken.
 */

export interface MarketplaceProduct {
  id: string;
  title: string;
  thumbnail: string;
  slug: string;
  price: number;
  created_at: string;
  category: string;
  view_count: number | null;
}

export type SortKey = 'new' | 'popular' | 'price-asc' | 'price-desc' | 'free';

export interface SortOption {
  key: SortKey;
  label: string;
  title: string;
}

export const SORT_OPTIONS: SortOption[] = [
  { key: 'new', label: 'Newest', title: 'Sort by newest first' },
  { key: 'popular', label: 'Most viewed', title: 'Sort by most viewed' },
  {
    key: 'price-asc',
    label: 'Price: low to high',
    title: 'Sort paid products from cheapest'
  },
  {
    key: 'price-desc',
    label: 'Price: high to low',
    title: 'Sort paid products from most expensive'
  },
  { key: 'free', label: 'Free', title: 'Show only free products' }
];

export const SORT_KEYS: SortKey[] = SORT_OPTIONS.map((option) => option.key);

export const DEFAULT_SORT: SortKey = 'new';

/** True when a product is genuinely free (price 0, blank or unparseable). */
export const isFreeProduct = (product: MarketplaceProduct): boolean => {
  const price = Number(product.price);
  return !Number.isFinite(price) || price <= 0;
};

/** Price label for a card. Free products show "Free" rather than "$0.00". */
export const formatPrice = (product: MarketplaceProduct): string =>
  isFreeProduct(product) ? 'Free' : `$${Number(product.price).toFixed(2)}`;

const byNewest = (a: MarketplaceProduct, b: MarketplaceProduct): number =>
  new Date(b.created_at).getTime() - new Date(a.created_at).getTime();

const byViewsDesc = (a: MarketplaceProduct, b: MarketplaceProduct): number =>
  (Number(b.view_count) || 0) - (Number(a.view_count) || 0);

/**
 * Apply one sort key to an already category/search filtered list.
 *
 * "Most viewed" ranks on view_count because PokeShani has no sales counter.
 * Labelling it "Most viewed" rather than "Best selling" keeps the claim true.
 * Ties fall back to newest so the order is always stable.
 */
export const sortProducts = (
  products: MarketplaceProduct[],
  sort: SortKey
): MarketplaceProduct[] => {
  switch (sort) {
    case 'free':
      return products.filter(isFreeProduct).sort(byNewest);

    case 'price-asc':
      return products
        .filter((product) => !isFreeProduct(product))
        .sort(
          (a, b) => Number(a.price) - Number(b.price) || byNewest(a, b)
        );

    case 'price-desc':
      return products
        .filter((product) => !isFreeProduct(product))
        .sort(
          (a, b) => Number(b.price) - Number(a.price) || byNewest(a, b)
        );

    case 'popular':
      return [...products].sort(
        (a, b) => byViewsDesc(a, b) || byNewest(a, b)
      );

    case 'new':
    default:
      return [...products].sort(byNewest);
  }
};

/** Narrow an arbitrary query-string value to a known sort key. */
export const isSortKey = (value: string | null | undefined): value is SortKey =>
  !!value && (SORT_KEYS as string[]).includes(value);

/** Resolve a raw ?sort= value to a usable sort key, defaulting sensibly. */
export const resolveSortKey = (value: string | null | undefined): SortKey =>
  isSortKey(value) ? value : DEFAULT_SORT;

/**
 * Category list for the dropdown, with "all" first and the rest alphabetical.
 * "all" is a UI sentinel, never a real category.
 */
export const buildCategoryOptions = (
  products: MarketplaceProduct[]
): string[] => {
  const unique = Array.from(
    new Set(
      products
        .map((product) => (product.category || '').trim())
        .filter(Boolean)
    )
  ).sort((a, b) => a.localeCompare(b));

  return ['all', ...unique];
};

/** Free and paid counts, used for the filter badges and the page copy. */
export const countByPrice = (
  products: MarketplaceProduct[]
): { free: number; paid: number } => {
  let free = 0;
  products.forEach((product) => {
    if (isFreeProduct(product)) free += 1;
  });
  return { free, paid: products.length - free };
};